import {
  ReactNode,
  SyntheticEvent,
  useCallback,
  useEffect,
  useState,
} from "react";
import ReactDOM from "react-dom";
import classNames from "classnames";
import { Checkbox } from "primereact/checkbox";
import {
  ARROW_DOWN_KEY,
  ARROW_UP_KEY,
  ENTER_KEY,
  ESCAPE_KEY,
  TAB_KEY,
} from "../../../../core/constants/keyboard_keys";
import {
  IDropdownWithCheckboxes,
  useDropdownWithCheckboxHooks,
} from "../../../utils/dropdown-hook";
import DropdownBody from "../DropdownBody";
import { ICheckbox } from "../../Checkbox/Checkbox";
import {
  getDropdownBodyHeight,
  getRootPopup,
} from "../../../../core/helpers/dropdownHelper";
import Chips, { IChips } from "../../Chips/Chips";
import Icon from "../../Icon";
import useTranslations from "../../../../core/i18n/useTranslations";

import baseStyles from "../Dropdown.module.css";
import styles from "./style.module.css";

export default function DropdownWIthCheckboxes(
  props: Readonly<IDropdownWithCheckboxes>,
) {
  const t = useTranslations();
  const {
    items,
    id,
    width,
    containerWidth,
    getSelectedItems,
    title,
    isOpen,
    setIsOpen,
    btnRef,
    setFocusedIndex,
    focusedIndex,
    container,
    withFilter,
    maxVisible,
    closeDropdown,
    hasError,
  } = useDropdownWithCheckboxHooks(props, true);
  const [checkboxItems, setCheckboxItems] = useState<ICheckbox[]>([]);
  const [filtredItems, setFiltredItems] = useState<ICheckbox[]>([]);
  const [selectedItems, setSelectedItems] = useState<ICheckbox[]>([]);

  const onChange = (item: ICheckbox) => {
    const options = [...checkboxItems];
    options.forEach((i) => {
      if (i?.id === item?.id) i.checked = !i.checked;
    });
    setCheckboxItems(options);

    const selectedOptions = options.filter((sO) => sO.checked);

    setSelectedItems(selectedOptions); // set items for chips
    getSelectedItems(selectedOptions); // return selected items to parent fro api calls for example
  };

  const unCheckItem = (item: IChips, e: SyntheticEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onChange({ ...item, checked: true });
  };

  const getChipsList = (): ReactNode | null => {
    return selectedItems.map((selectedItem, index) => {
      if (index > 2) return null;
      return (
        <Chips
          item={{
            id: selectedItem.id,
            label: selectedItem.label,
          }}
          key={selectedItem.id}
          onRemove={unCheckItem}
        />
      );
    });
  };

  const handleInputKeyDown = useCallback(
    (e: any) => {
      const { key } = e;
      switch (key) {
        case ARROW_UP_KEY:
          e.preventDefault();
          if (!filtredItems.length) return;
          setFocusedIndex((prev) => {
            return {
              idx:
                focusedIndex.idx <= 0 ? filtredItems.length - 1 : prev.idx - 1,
              avoidScroll: false,
            };
          });
          break;
        case ARROW_DOWN_KEY:
          e.preventDefault();
          if (!filtredItems.length) return;
          setFocusedIndex((p) => {
            return {
              idx: (p.idx + 1) % filtredItems.length,
              avoidScroll: false,
            };
          });
          break;
        case ESCAPE_KEY:
          closeDropdown(true);
          setFocusedIndex({ idx: -1, avoidScroll: false });
          break;
        case ENTER_KEY:
          if (isOpen) onChange(filtredItems[focusedIndex.idx]);
          else setIsOpen(true);
          break;
        case TAB_KEY:
          if (isOpen) closeDropdown(true);
          break;
        default:
          break;
      }
    },
    [isOpen, focusedIndex, filtredItems.length],
  );

  const onFilter = (filter: string) => {
    const filtered = items.filter(
      (item) => item.label?.toLowerCase().indexOf(filter.toLowerCase()) > -1,
    );
    setFiltredItems(filtered);
  };

  const renderItems = () =>
    filtredItems.map((item: ICheckbox, idx: number) => {
      return (
        <div
          key={item.id}
          role="option"
          aria-selected={item.checked}
          tabIndex={idx === focusedIndex.idx ? 0 : -1}
          className={classNames(styles.chOption, "option", {
            [styles.focused]: focusedIndex.idx === idx,
            [styles.selected]: item.checked,
          })}
          onKeyDown={handleInputKeyDown}
          onClick={() => onChange(item)}
          onMouseMove={() =>
            setFocusedIndex((prev) => {
              return prev.idx === idx ? prev : { idx, avoidScroll: true };
            })
          }
        >
          <Checkbox
            id={id}
            tabIndex={-1}
            inputId={item.label}
            name={item.label}
            value={item.label}
            checked={item.checked}
            className={styles.checkbox}
            aria-selected={item.checked}
          />
          <span className={styles.optionLabel}>{item.label}</span>
        </div>
      );
    });

  useEffect(() => {
    const selected = items.filter((sO) => sO.checked);
    setSelectedItems(selected);
  }, [items]);

  useEffect(() => {
    setCheckboxItems(items);
    setFiltredItems(items);
  }, [items]);

  return (
    <div id={id} style={{ width: containerWidth }}>
      {title && <div className={styles.title}>{title}</div>}
      <button
        type="button"
        ref={btnRef}
        className={classNames({
          [baseStyles.dropdownBtn]: true,
          [baseStyles.inputValidationError]: hasError,
        })}
        style={{ width }}
        onClick={(e) => {
          if (e.type !== "click") return;
          e.preventDefault();
          setIsOpen(!isOpen);
        }}
        onKeyDown={handleInputKeyDown}
      >
        <div className={styles.dropdownBtnItems}>
          {selectedItems.length === 0 ? (
            <div className={baseStyles.selectOption}>
              {props.placeholder ?? t("Common:select_option")}
            </div>
          ) : (
            <div className={styles.chipsList}>{getChipsList()}</div>
          )}
          {selectedItems.length > 3 && (
            <div className={styles.moreItems}>+{selectedItems.length - 3}</div>
          )}
        </div>

        {isOpen ? (
          <Icon name="chevron-up" size={14} />
        ) : (
          <Icon name="chevron-down" size={14} />
        )}
      </button>
      {isOpen &&
        ReactDOM.createPortal(
          <DropdownBody
            withFilter={withFilter}
            onFilter={onFilter}
            elem={btnRef.current}
            innerRef={container}
            onClose={() => closeDropdown(true)}
            dropDownHeight={getDropdownBodyHeight(
              items.length,
              window.innerHeight <= 600 ? 6 : maxVisible,
              withFilter,
            )}
            keyHandler={handleInputKeyDown}
          >
            {renderItems()}
          </DropdownBody>,
          getRootPopup(),
        )}
    </div>
  );
}
